import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api';
import { io } from 'socket.io-client';
import axios from 'axios';
import { toast } from 'sonner';
import { LogOut, Power, MapPin, Navigation, Clock, DollarSign, Check, X, History, User, MessageCircle, Menu, Settings, List, Map, Minimize2, UserCircle, RefreshCw, AlertCircle, Globe, ExternalLink, HelpCircle, Pencil, AtSign, Phone, Mail, Calendar, ChevronUp, ChevronDown, Route, GripVertical, AlertTriangle, Play, Coffee, Repeat } from 'lucide-react';
import ChatBox from '@/components/ChatBox';
import NotificationCenter from '@/components/NotificationCenter';
import { useLocationBackground } from '@/components/LocationBackground';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SOCKET_URL = process.env.REACT_APP_BACKEND_URL;
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const libraries = ['places'];

// Google Maps dark theme styles
const mapStyles = [
  { elementType: 'geometry', stylers: [{ color: '#242f3e' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#242f3e' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#746855' }] },
  { featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: '#d59563' }] },
  { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#d59563' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#263c3f' }] },
  { featureType: 'poi.park', elementType: 'labels.text.fill', stylers: [{ color: '#6b9a76' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#38414e' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#212a37' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#9ca5b3' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#746855' }] },
  { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#1f2835' }] },
  { featureType: 'road.highway', elementType: 'labels.text.fill', stylers: [{ color: '#f3d19c' }] },
  { featureType: 'transit', elementType: 'geometry', stylers: [{ color: '#2f3948' }] },
  { featureType: 'transit.station', elementType: 'labels.text.fill', stylers: [{ color: '#d59563' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#17263c' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#515c6d' }] },
  { featureType: 'water', elementType: 'labels.text.stroke', stylers: [{ color: '#17263c' }] },
];

export default function DriverDashboard() {
  const { user, logout, refreshUser, switchRole } = useAuth();
  const navigate = useNavigate();
  const { cityName } = useLocationBackground();
  const [available, setAvailable] = useState(false);
  const [activeRide, setActiveRide] = useState(null);
  const [nearbyRides, setNearbyRides] = useState([]);
  const [rideHistory, setRideHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showAccountSettings, setShowAccountSettings] = useState(false);
  const [isEditingAccount, setIsEditingAccount] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [selectedRidePopup, setSelectedRidePopup] = useState(null);
  const [switchingRole, setSwitchingRole] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [pendingAssignedRides, setPendingAssignedRides] = useState([]); // Rides where rider specifically requested this driver
  const [accountForm, setAccountForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    username: '',
    phone: '',
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const socketRef = useRef(null);
  const locationIntervalRef = useRef(null);
  const mapRef = useRef(null);

  // Google Maps loader
  const { isLoaded: mapsLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries,
  });

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  const [mapCenter, setMapCenter] = useState([37.6872, -97.3301]);
  const [currentLocation, setCurrentLocation] = useState({ latitude: 37.6872, longitude: -97.3301 });
  const [locationInitialized, setLocationInitialized] = useState(false);
  const [fullScreenMap, setFullScreenMap] = useState(false);

  // Scheduled Rides State
  const [showScheduledRides, setShowScheduledRides] = useState(false);
  const [scheduledRides, setScheduledRides] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [optimizedRoute, setOptimizedRoute] = useState([]);
  const [showRouteMap, setShowRouteMap] = useState(false);
  const [draggedRide, setDraggedRide] = useState(null);

  const handleSwitchRole = async () => {
    if (activeRide) {
      toast.error('Cannot switch roles while you have an active ride');
      return;
    }
    if (available) {
      toast.error('Please go offline before switching to Rider mode');
      return;
    }
    
    setSwitchingRole(true);
    try {
      const updatedUser = await switchRole();
      toast.success(`Switched to ${updatedUser.role} mode!`);
      navigate(`/${updatedUser.role}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to switch role');
    } finally {
      setSwitchingRole(false);
    }
  };

  useEffect(() => {
    fetchActiveRide();
    fetchRideHistory();
    fetchDriverStatus(); // Fetch current online/offline status from backend

    // Get user's actual location using geolocation API
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setCurrentLocation({ latitude, longitude });
          setMapCenter([latitude, longitude]);
          setLocationInitialized(true);
          console.log('Driver location initialized:', latitude, longitude);
        },
        (error) => {
          console.warn('Geolocation error:', error.message);
          // Keep default Wichita location if geolocation fails - no error toast on initial load
          setLocationInitialized(true);
        },
        {
          enableHighAccuracy: false, // Try low accuracy first for faster response
          timeout: 15000,
          maximumAge: 300000 // Cache for 5 minutes
        }
      );
    } else {
      setLocationInitialized(true);
    }

    // Set initial account form data
    const names = user.name?.split(' ') || ['', ''];
    setAccountForm({
      first_name: names[0] || '',
      last_name: names.slice(1).join(' ') || '',
      email: user.email || '',
      username: user.username || '',
      phone: user.phone || '',
      current_password: '',
      new_password: '',
      confirm_password: '',
    });

    // Setup Socket.IO
    socketRef.current = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
    });

    socketRef.current.on('connect', () => {
      console.log('Driver connected to socket');
      socketRef.current.emit('join', { user_id: user.id, role: user.role });
    });

    socketRef.current.on('new_ride', (ride) => {
      console.log('New ride available:', ride);
      fetchNearbyRides();
      toast.info('New ride request available!');
    });

    // Listen for assigned ride requests
    socketRef.current.on('ride_request_assigned', (ride) => {
      console.log('Ride request assigned to you:', ride);
      fetchPendingAssignedRides();
      toast.info(`${ride.rider_name} has requested you for a scheduled ride!`, {
        duration: 10000,
        action: {
          label: 'View',
          onClick: () => setShowScheduledRides(true)
        }
      });
    });

    socketRef.current.on('ride_update', (ride) => {
      console.log('Ride update:', ride);
      
      // Check if this ride update is for the current driver
      const isDriverRide = ride.driver_id === user.id;
      
      // End ride when arrived, completed, or cancelled - reset to default state
      if (ride.status === 'arrived' || ride.status === 'completed' || ride.status === 'cancelled') {
        // Only process if this is the driver's ride
        if (isDriverRide) {
          const message = ride.status === 'arrived' 
            ? 'Ride completed! Rider has been dropped off.' 
            : `Ride ${ride.status}`;
          toast.success(message);
          
          // Reset ride-related state but KEEP driver online
          setActiveRide(null);
          setShowChat(false);
          setUnreadMessages(0);
          setShowHistory(false);
          setShowCancelModal(false);
          setCancelReason('');
          setSelectedRidePopup(null);
          setMapCenter([37.6872, -97.3301]); // Reset to default Wichita
          
          // Refresh data - get new ride history and nearby rides
          // Driver stays online, so fetch nearby rides automatically
          fetchRideHistory();
          fetchNearbyRides();
        }
      } else if (isDriverRide) {
        // Update active ride for any status change (accepted, en_route, etc.)
        setActiveRide(ride);
        // Also clear nearby rides since driver now has an active ride
        setNearbyRides([]);
      }
    });

    // Listen for new messages and show notification if chat is closed
    socketRef.current.on('new_message', (message) => {
      console.log('New message received:', message);
      // Only increment unread count if message is from rider
      if (message.sender_role === 'rider') {
        setUnreadMessages(prev => prev + 1);
      }
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
      if (locationIntervalRef.current) {
        clearInterval(locationIntervalRef.current);
      }
    };
  }, [user.id, user.role]);

  useEffect(() => {
    if (available && !activeRide) {
      fetchNearbyRides();
    }
  }, [available]);

  useEffect(() => {
    if (available && activeRide && ['accepted', 'en_route', 'arrived', 'in_progress'].includes(activeRide.status)) {
      // Simulate location updates
      locationIntervalRef.current = setInterval(() => {
        updateLocation();
      }, 5000);
    } else {
      if (locationIntervalRef.current) {
        clearInterval(locationIntervalRef.current);
        locationIntervalRef.current = null;
      }
    }

    return () => {
      if (locationIntervalRef.current) {
        clearInterval(locationIntervalRef.current);
      }
    };
  }, [available, activeRide]);

  const fetchActiveRide = async () => {
    try {
      const response = await axios.get(`${API}/rides/active`);
      if (response.data) {
        setActiveRide(response.data);
        // If driver has an active ride, they should be shown as online
        setAvailable(true);
      }
    } catch (error) {
      console.error('Failed to fetch active ride:', error);
    }
  };

  const fetchDriverStatus = async () => {
    try {
      // Get current user data which includes available status
      const response = await axios.get(`${API}/auth/me`);
      if (response.data && response.data.available !== undefined) {
        setAvailable(response.data.available || false);
        // If online, fetch nearby rides
        if (response.data.available) {
          fetchNearbyRides();
        }
      }
    } catch (error) {
      console.error('Failed to fetch driver status:', error);
    }
  };

  const fetchNearbyRides = async () => {
    try {
      const response = await axios.get(`${API}/rides/nearby`);
      setNearbyRides(response.data);
    } catch (error) {
      console.error('Failed to fetch nearby rides:', error);
    }
  };

  const fetchRideHistory = async () => {
    try {
      const response = await axios.get(`${API}/rides/history`);
      setRideHistory(response.data);
    } catch (error) {
      console.error('Failed to fetch ride history:', error);
    }
  };

  // Fetch scheduled rides for the driver
  const fetchScheduledRides = async () => {
    try {
      const response = await axios.get(`${API}/rides/scheduled`);
      const rides = response.data || [];
      setScheduledRides(rides);
      if (rides.length > 0) {
        optimizeRouteOrder(rides);
      }
    } catch (error) {
      console.error('Failed to fetch scheduled rides:', error);
    }
  };

  // Fetch rides where riders have specifically requested this driver
  const fetchPendingAssignedRides = async () => {
    try {
      const response = await axios.get(`${API}/drivers/${user.id}/rides-pending`);
      setPendingAssignedRides(response.data || []);
    } catch (error) {
      console.error('Failed to fetch pending assigned rides:', error);
    }
  };

  // Accept an assigned ride request
  const handleAcceptAssignedRide = async (rideId) => {
    try {
      await axios.post(`${API}/rides/${rideId}/accept`);
      toast.success('Ride accepted! Added to your scheduled rides.');
      fetchPendingAssignedRides();
      fetchScheduledRides();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept ride');
    }
  };

  // Decline an assigned ride request
  const handleDeclineAssignedRide = async (rideId, reason = 'Not available at this time') => {
    try {
      await axios.post(`${API}/rides/${rideId}/decline-assignment`, { reason });
      toast.success('Ride request declined. The rider will be notified.');
      fetchPendingAssignedRides();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to decline ride');
    }
  };

  // Calculate distance between two points (Haversine formula)
  const calculateDistanceBetween = (lat1, lon1, lat2, lon2) => {
    const R = 3959; // Earth's radius in miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  // Optimize route order based on distance and scheduled times
  const optimizeRouteOrder = (rides) => {
    if (!rides || rides.length === 0) return;
    
    // Sort rides by scheduled time first, then optimize within time windows
    const sortedRides = [...rides].sort((a, b) => {
      const timeA = a.scheduled_time ? new Date(a.scheduled_time).getTime() : Date.now();
      const timeB = b.scheduled_time ? new Date(b.scheduled_time).getTime() : Date.now();
      return timeA - timeB;
    });

    // Create optimized route considering current location and ride locations
    const optimized = [];
    let currentPos = { lat: currentLocation.latitude, lng: currentLocation.longitude };
    const remaining = [...sortedRides];

    while (remaining.length > 0) {
      // Find the next best ride considering both time and distance
      let bestIndex = 0;
      let bestScore = Infinity;

      remaining.forEach((ride, index) => {
        const distance = calculateDistanceBetween(
          currentPos.lat, currentPos.lng,
          ride.pickup_lat, ride.pickup_lng
        );
        const timeUntilPickup = ride.scheduled_time 
          ? (new Date(ride.scheduled_time).getTime() - Date.now()) / (1000 * 60) // minutes
          : 0;
        
        // Score: lower is better (balance distance and time urgency)
        const score = distance + (timeUntilPickup < 30 ? -50 : 0); // Prioritize urgent rides
        
        if (score < bestScore) {
          bestScore = score;
          bestIndex = index;
        }
      });

      const nextRide = remaining.splice(bestIndex, 1)[0];
      optimized.push(nextRide);
      currentPos = { lat: nextRide.dropoff_lat, lng: nextRide.dropoff_lng };
    }

    setOptimizedRoute(optimized);
  };

  // Check for ride conflicts (overlapping times)
  const checkRideConflicts = (rides) => {
    const conflicts = [];
    const sortedRides = [...rides].sort((a, b) => {
      const timeA = a.scheduled_time ? new Date(a.scheduled_time).getTime() : 0;
      const timeB = b.scheduled_time ? new Date(b.scheduled_time).getTime() : 0;
      return timeA - timeB;
    });

    for (let i = 0; i < sortedRides.length - 1; i++) {
      const currentRide = sortedRides[i];
      const nextRide = sortedRides[i + 1];
      
      if (!currentRide.scheduled_time || !nextRide.scheduled_time) continue;
      
      const currentEndTime = new Date(currentRide.scheduled_time).getTime() + 30 * 60 * 1000; // Assume 30 min per ride
      const nextStartTime = new Date(nextRide.scheduled_time).getTime();
      
      // Calculate travel time between rides (rough estimate: 2 min per mile)
      const travelDistance = calculateDistanceBetween(
        currentRide.dropoff_lat, currentRide.dropoff_lng,
        nextRide.pickup_lat, nextRide.pickup_lng
      );
      const travelTimeMs = travelDistance * 2 * 60 * 1000;
      
      if (currentEndTime + travelTimeMs > nextStartTime) {
        conflicts.push({
          ride1: currentRide,
          ride2: nextRide,
          overlapMinutes: Math.round((currentEndTime + travelTimeMs - nextStartTime) / (60 * 1000))
        });
      }
    }
    return conflicts;
  };

  // Manually reorder rides
  const moveRide = (fromIndex, toIndex) => {
    const newOrder = [...scheduledRides];
    const [movedItem] = newOrder.splice(fromIndex, 1);
    newOrder.splice(toIndex, 0, movedItem);
    setScheduledRides(newOrder);
    
    // Check for conflicts after reordering
    const conflicts = checkRideConflicts(newOrder);
    if (conflicts.length > 0) {
      toast.warning(`Warning: ${conflicts.length} potential time conflict(s) detected`);
    }
  };

  // Calculate total estimated earnings for scheduled rides
  const calculateTotalEarnings = () => {
    return scheduledRides.reduce((total, ride) => {
      return total + (ride.custom_fare || ride.fare || 0);
    }, 0);
  };

  // Get rides for selected date
  const getRidesForDate = (date) => {
    return scheduledRides.filter(ride => {
      if (!ride.scheduled_time) return false;
      const rideDate = new Date(ride.scheduled_time).toISOString().split('T')[0];
      return rideDate === date;
    });
  };

  // Fetch scheduled rides on mount and when availability changes
  useEffect(() => {
    fetchScheduledRides();
    fetchPendingAssignedRides();
  }, []);


  const toggleAvailability = async () => {
    try {
      await axios.post(`${API}/drivers/availability`, { available: !available });
      setAvailable(!available);
      toast.success(`You are now ${!available ? 'online' : 'offline'}`);
      if (!available) {
        fetchNearbyRides();
      } else {
        setNearbyRides([]);
      }
    } catch (error) {
      toast.error('Failed to update availability');
    }
  };

  const updateLocation = async () => {
    // Use real geolocation to get current position
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          const newLocation = { latitude, longitude };
          setCurrentLocation(newLocation);
          setMapCenter([latitude, longitude]);

          try {
            await axios.post(`${API}/drivers/location`, newLocation);
          } catch (error) {
            console.error('Failed to update location:', error);
          }
        },
        (error) => {
          console.warn('Geolocation error during update:', error.message);
          // If geolocation fails, still try to send current stored location
          axios.post(`${API}/drivers/location`, currentLocation).catch(console.error);
        },
        {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0
        }
      );
    } else {
      // Fallback: send current stored location
      try {
        await axios.post(`${API}/drivers/location`, currentLocation);
      } catch (error) {
        console.error('Failed to update location:', error);
      }
    }
  };

  const canAcceptRide = (ride) => {
    // If driver has no active ride, they can accept
    if (!activeRide) return { canAccept: true };
    
    // If driver has an active ride that's not completed/cancelled, check conditions
    if (activeRide && !['completed', 'cancelled'].includes(activeRide.status)) {
      // Check if the new ride is scheduled for the future
      if (ride.ride_type === 'scheduled' && ride.scheduled_time) {
        const scheduledTime = new Date(ride.scheduled_time);
        const now = new Date();
        const timeDiffMinutes = (scheduledTime - now) / (1000 * 60);
        
        // Need at least 4 minutes buffer
        if (timeDiffMinutes >= 4) {
          return { canAccept: true };
        } else {
          return { 
            canAccept: false, 
            reason: `Scheduled ride must be at least 4 minutes in the future. This ride starts in ${Math.round(timeDiffMinutes)} minutes.`
          };
        }
      }
      
      return { 
        canAccept: false, 
        reason: 'You already have an active ride. Complete it first or only accept scheduled rides with 4+ minute buffer.'
      };
    }
    
    return { canAccept: true };
  };

  // Open Google Maps with full navigation route: Driver Location -> Pickup -> Dropoff
  const openGoogleMapsNavigation = () => {
    if (!activeRide) return;
    
    // Always show full route: current location -> pickup (waypoint) -> dropoff (destination)
    // This gives driver complete visibility of the entire trip
    const url = `https://www.google.com/maps/dir/?api=1&origin=${currentLocation.latitude},${currentLocation.longitude}&waypoints=${activeRide.pickup_lat},${activeRide.pickup_lng}&destination=${activeRide.dropoff_lat},${activeRide.dropoff_lng}&travelmode=driving`;
    
    window.open(url, '_blank');
  };

  const handleAcceptRide = async (rideId) => {
    const ride = nearbyRides.find(r => r.id === rideId);
    if (!ride) {
      toast.error('Ride not found');
      return;
    }
    
    const { canAccept, reason } = canAcceptRide(ride);
    if (!canAccept) {
      toast.error(reason);
      return;
    }
    
    try {
      const response = await axios.post(`${API}/rides/${rideId}/accept`);
      // Immediately update state to show active ride
      setActiveRide(response.data);
      setNearbyRides([]); // Clear nearby rides since we now have an active ride
      setSelectedRidePopup(null);
      toast.success('Ride accepted!');
      setMapCenter([response.data.pickup_lat, response.data.pickup_lng]);
      
      // Ensure driver is shown as online
      if (!available) {
        setAvailable(true);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept ride');
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    if (!activeRide) return;
    try {
      await axios.post(`${API}/rides/${activeRide.id}/update-status`, { status: newStatus });
      toast.success(`Ride status updated to ${newStatus.replace('_', ' ')}`);
    } catch (error) {
      toast.error('Failed to update ride status');
    }
  };

  const handleRejectRide = async () => {
    if (!activeRide || !cancelReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    try {
      await axios.post(`${API}/rides/${activeRide.id}/reject`, { reason: cancelReason });
      toast.success('Ride rejected - returned to available rides');
      setActiveRide(null);
      setShowCancelModal(false);
      setCancelReason('');
      fetchRideHistory();
      fetchNearbyRides();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject ride');
    }
  };

  const handleUpdateAccount = async () => {
    // Validate required fields
    if (!accountForm.first_name.trim() || !accountForm.last_name.trim()) {
      toast.error('First name and last name are required');
      return;
    }
    
    if (!accountForm.current_password) {
      toast.error('Current password is required to save changes');
      return;
    }

    // Validate email format
    if (accountForm.email && !accountForm.email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    // Validate username
    if (accountForm.username && accountForm.username.trim().length < 3) {
      toast.error('Username must be at least 3 characters');
      return;
    }

    // Validate phone
    if (accountForm.phone && accountForm.phone.trim().length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }
    
    try {
      const updateData = {
        first_name: accountForm.first_name.trim(),
        last_name: accountForm.last_name.trim(),
        email: accountForm.email.trim(),
        username: accountForm.username.trim(),
        phone: accountForm.phone.trim(),
        current_password: accountForm.current_password,
      };
      
      if (accountForm.new_password) {
        if (accountForm.new_password !== accountForm.confirm_password) {
          toast.error('New passwords do not match');
          return;
        }
        if (accountForm.new_password.length < 6) {
          toast.error('New password must be at least 6 characters');
          return;
        }
        updateData.new_password = accountForm.new_password;
      }
      
      await axios.patch(`${API}/users/profile`, updateData);
      toast.success('Account updated successfully!');
      
      // Refresh user data to update UI immediately
      await refreshUser();
      
      // Reset password fields
      setAccountForm(prev => ({
        ...prev,
        current_password: '',
        new_password: '',
        confirm_password: '',
      }));
      
      setIsEditingAccount(false);
      setShowAccountSettings(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update account');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      requested: 'text-yellow-400',
      accepted: 'text-blue-400',
      en_route: 'text-purple-400',
      arrived: 'text-green-400',
      in_progress: 'text-neon-cyan',
      completed: 'text-green-500',
      cancelled: 'text-red-400',
    };
    return colors[status] || 'text-white';
  };

  const formatStatus = (status) => {
    return status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const getNextStatusAction = () => {
    if (!activeRide) return null;
    // Simplified flow: accepted → en_route → arrived (completes the ride)
    const statusFlow = {
      accepted: { next: 'en_route', label: 'Start Trip', icon: Navigation },
      en_route: { next: 'arrived', label: 'Mark Arrived (Complete)', icon: Check },
    };
    return statusFlow[activeRide.status];
  };

  return (
    <div className="h-screen bg-dark-bg text-white relative overflow-hidden">
      {/* Map Background - Fixed */}
      <div className={`fixed inset-0 ${fullScreenMap ? 'z-50' : 'z-0'}`}>
        {mapsLoaded ? (
          <GoogleMap
            mapContainerStyle={{ height: '100%', width: '100%' }}
            center={{ lat: mapCenter[0], lng: mapCenter[1] }}
            zoom={13}
            onLoad={onMapLoad}
            options={{
              disableDefaultUI: !fullScreenMap,
              zoomControl: fullScreenMap,
              streetViewControl: false,
              mapTypeControl: false,
              fullscreenControl: false,
              styles: mapStyles,
            }}
          >
            {/* Driver's Current Location */}
            <Marker 
              position={{ lat: currentLocation.latitude, lng: currentLocation.longitude }}
              icon={{ url: 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png' }}
              title="Your Location"
            />
            
            {/* Active Ride Markers */}
            {activeRide && (
              <>
                <Marker 
                  position={{ lat: activeRide.pickup_lat, lng: activeRide.pickup_lng }}
                  icon={{ url: 'https://maps.google.com/mapfiles/ms/icons/green-dot.png' }}
                  title={`Pickup: ${activeRide.pickup_address}`}
                />
                <Marker 
                  position={{ lat: activeRide.dropoff_lat, lng: activeRide.dropoff_lng }}
                  icon={{ url: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png' }}
                  title={`Dropoff: ${activeRide.dropoff_address}`}
                />
              </>
            )}
            
            {/* Nearby Ride Request Markers */}
            {available && nearbyRides.map((ride) => (
              <Marker
                key={ride.id}
                position={{ lat: ride.pickup_lat, lng: ride.pickup_lng }}
                icon={{ url: 'https://maps.google.com/mapfiles/ms/icons/yellow-dot.png' }}
                title={`Ride Request: $${ride.fare} - ${ride.distance?.toFixed(1)} miles`}
                onClick={() => setSelectedRidePopup(ride)}
              />
            ))}
          </GoogleMap>
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-dark-surface">
            <div className="text-neon-purple animate-pulse">Loading Map...</div>
          </div>
        )}
        
        {/* Full Screen Map Close Button */}
        {fullScreenMap && (
          <div className="absolute top-4 right-4 z-[1000]">
            <button
              onClick={() => setFullScreenMap(false)}
              className="bg-dark-surface hover:bg-dark-subtle text-white p-3 rounded-full shadow-lg transition-all"
              data-testid="close-fullscreen-map-btn"
            >
              <Minimize2 className="w-6 h-6" />
            </button>
          </div>
        )}
        
        {/* Full Screen Map Legend */}
        {fullScreenMap && (
          <div className="absolute bottom-4 left-4 z-[1000] bg-dark-surface/90 backdrop-blur-sm rounded-xl p-4">
            <h4 className="text-white font-bold mb-2 text-sm">Map Legend</h4>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-blue-500"></div>
                <span className="text-white/80">Your Location</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-green-500"></div>
                <span className="text-white/80">Ride Pickups ({nearbyRides.length})</span>
              </div>
              {activeRide && (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-red-500"></div>
                  <span className="text-white/80">Dropoff</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Ride Details Popup Overlay */}
      {selectedRidePopup && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-dark-surface rounded-2xl p-6 w-full max-w-md border border-white/20 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-outfit font-bold text-xl text-white">Ride Details</h3>
              <button
                onClick={() => setSelectedRidePopup(null)}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Distance & Fare */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-dark-bg rounded-xl p-4 text-center">
                  <div className="text-white/60 text-sm mb-1">Distance</div>
                  <div className="text-2xl font-bold text-white">{selectedRidePopup.distance?.toFixed(1)}</div>
                  <div className="text-white/60 text-xs">miles</div>
                </div>
                <div className="bg-dark-bg rounded-xl p-4 text-center">
                  <div className="text-white/60 text-sm mb-1">Fare</div>
                  <div className="text-2xl font-bold text-neon-cyan">${selectedRidePopup.fare}</div>
                  <div className="text-white/60 text-xs">total</div>
                </div>
              </div>
              
              {/* Ride Info */}
              <div className="bg-dark-bg rounded-xl p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-xs text-white/60">Pickup</div>
                    <div className="text-sm text-white">{selectedRidePopup.pickup_address}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Navigation className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-xs text-white/60">Dropoff</div>
                    <div className="text-sm text-white">{selectedRidePopup.dropoff_address}</div>
                  </div>
                </div>
                {selectedRidePopup.rider_name && (
                  <div className="flex items-start gap-3">
                    <User className="w-5 h-5 text-neon-cyan mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-xs text-white/60">Rider</div>
                      <div className="text-sm text-white">{selectedRidePopup.rider_name}</div>
                    </div>
                  </div>
                )}
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-xs text-white/60">ETA</div>
                    <div className="text-sm text-white">{selectedRidePopup.eta} min</div>
                  </div>
                </div>
                {selectedRidePopup.seats_requested > 1 && (
                  <div className="flex items-start gap-3">
                    <User className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-xs text-white/60">Seats Requested</div>
                      <div className="text-sm text-white">{selectedRidePopup.seats_requested} seats</div>
                    </div>
                  </div>
                )}
                {selectedRidePopup.ride_type === 'scheduled' && (
                  <div className="flex items-start gap-3">
                    <Clock className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-xs text-white/60">Scheduled For</div>
                      <div className="text-sm text-white">{new Date(selectedRidePopup.scheduled_time).toLocaleString()}</div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Warning if driver has active ride */}
              {activeRide && !['completed', 'cancelled'].includes(activeRide.status) && (
                <div className="bg-yellow-500/20 border border-yellow-500/30 rounded-xl p-3">
                  <p className="text-yellow-400 text-sm">
                    ⚠️ You have an active ride. You can only accept scheduled rides with at least 4 minutes buffer time.
                  </p>
                </div>
              )}
              
              {/* Accept Button */}
              <button
                onClick={() => handleAcceptRide(selectedRidePopup.id)}
                className="w-full bg-neon-cyan text-black hover:bg-[#00B8D4] rounded-full font-bold py-4 transition-all hover:scale-105 shadow-[0_0_15px_rgba(0,224,255,0.4)]"
                data-testid="accept-ride-popup-btn"
              >
                Accept Ride
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Scrollable Content Overlay */}
      <div className="relative z-10 h-screen overflow-y-auto pb-20 pointer-events-none">
      {/* Header */}
      <div className="relative z-50 px-4 sm:px-6 py-3 sm:py-4 pointer-events-auto">
        <div className="glass-panel rounded-2xl p-3 sm:p-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors flex-shrink-0"
              data-testid="menu-btn"
              title="Menu"
            >
              <Menu className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
            {/* Clickable Logo - navigates to home/dashboard */}
            <button
              onClick={() => navigate('/driver')}
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
              data-testid="logo-home-btn"
              title="Go to Dashboard"
            >
              <img src="/logo.png" alt="vgoride" className="w-8 h-8 sm:w-10 sm:h-10 object-contain" />
              <span className="font-outfit font-bold text-lg sm:text-xl text-neon-purple hidden xs:inline">vgoride</span>
            </button>
            <div className="min-w-0 hidden sm:block ml-2">
              <div className="font-medium truncate">{user.name}</div>
              <div className="flex items-center gap-1 text-xs text-white/60">
                <Globe className="w-3 h-3" />
                <span className="truncate">{cityName}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            <button
              onClick={toggleAvailability}
              className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-1.5 sm:py-2 rounded-full font-medium transition-all text-xs sm:text-sm ${
                available
                  ? 'bg-green-500 text-white'
                  : 'bg-white/10 text-white/60 hover:bg-white/20'
              }`}
              data-testid="availability-toggle-btn"
              title={available ? 'Go Offline' : 'Go Online'}
            >
              <Power className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span className="hidden xs:inline">{available ? 'Online' : 'Offline'}</span>
            </button>
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="p-1.5 sm:p-2 hover:bg-white/10 rounded-lg transition-colors"
              data-testid="history-btn"
              title="Ride History"
            >
              <History className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
            {/* Notification Center */}
            <NotificationCenter userId={user.id} userRole="driver" />
            <button
              onClick={() => setFullScreenMap(true)}
              className="p-1.5 sm:p-2 hover:bg-white/10 rounded-lg transition-colors"
              data-testid="map-view-btn"
              title="Map View"
            >
              <Map className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
            {/* Chat Icon - Only visible during active ride */}
            {activeRide && (
              <button
                onClick={() => {
                  setShowChat(true);
                  setUnreadMessages(0);
                }}
                className="p-1.5 sm:p-2 hover:bg-white/10 rounded-lg transition-colors relative"
                data-testid="header-chat-btn"
                title="Chat with Rider"
              >
                <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                {unreadMessages > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 sm:w-5 sm:h-5 bg-red-500 text-white text-[10px] sm:text-xs font-bold rounded-full flex items-center justify-center animate-pulse">
                    {unreadMessages > 9 ? '9+' : unreadMessages}
                  </span>
                )}
              </button>
            )}
            <button
              onClick={handleSwitchRole}
              disabled={switchingRole || activeRide || available}
              className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg transition-colors ${
                activeRide || available
                  ? 'bg-white/5 text-white/40 cursor-not-allowed' 
                  : 'bg-neon-cyan/20 text-neon-cyan hover:bg-neon-cyan/30'
              }`}
              data-testid="switch-role-btn"
              title={activeRide ? 'Complete active ride to switch' : available ? 'Go offline to switch' : 'Switch to Rider'}
            >
              {switchingRole ? (
                <RefreshCw className="w-3.5 h-3.5 sm:w-4 sm:h-4 animate-spin" />
              ) : (
                <UserCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              )}
              <span className="text-xs sm:text-sm font-medium hidden xs:inline">Ride</span>
            </button>
            <button
              onClick={logout}
              className="p-1.5 sm:p-2 hover:bg-white/10 rounded-lg transition-colors"
              data-testid="logout-btn"
              title="Sign Out"
            >
              <LogOut className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          </div>
        </div>

        {/* Dropdown Menu */}
        {showMenu && (
          <div className="absolute top-20 left-6 w-64 glass-panel rounded-2xl p-2 z-[100] shadow-xl">
            <button
              onClick={() => {
                setShowAccountSettings(true);
                setShowMenu(false);
              }}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/10 rounded-xl transition-colors text-left"
              data-testid="account-menu-btn"
            >
              <Settings className="w-5 h-5 text-neon-cyan" />
              <span>Account Settings</span>
            </button>
            <button
              onClick={() => {
                navigate('/driver/analytics');
                setShowMenu(false);
              }}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/10 rounded-xl transition-colors text-left"
              data-testid="analytics-menu-btn"
            >
              <DollarSign className="w-5 h-5 text-green-400" />
              <span>Earnings & Analytics</span>
            </button>
            <button
              onClick={() => {
                navigate('/rides-available');
                setShowMenu(false);
              }}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/10 rounded-xl transition-colors text-left"
              data-testid="all-rides-menu-btn"
            >
              <List className="w-5 h-5 text-neon-cyan" />
              <span>Rides Available</span>
            </button>
            <button
              onClick={() => {
                setShowScheduledRides(true);
                setShowMenu(false);
              }}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/10 rounded-xl transition-colors text-left"
              data-testid="scheduled-rides-menu-btn"
            >
              <Calendar className="w-5 h-5 text-purple-400" />
              <span>Scheduled Rides</span>
              {(scheduledRides.length > 0 || pendingAssignedRides.length > 0) && (
                <span className={`ml-auto text-white text-xs font-bold px-2 py-0.5 rounded-full ${
                  pendingAssignedRides.length > 0 ? 'bg-yellow-500 animate-pulse' : 'bg-purple-500'
                }`}>
                  {scheduledRides.length + pendingAssignedRides.length}
                </span>
              )}
            </button>
            <div className="border-t border-white/10 my-2"></div>
            <a
              href="mailto:customer-support@vgoride.net"
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/10 rounded-xl transition-colors text-left"
              data-testid="contact-support-btn"
            >
              <HelpCircle className="w-5 h-5 text-yellow-400" />
              <div>
                <span className="block">Contact Support</span>
                <span className="text-xs text-white/50">customer-support@vgoride.net</span>
              </div>
            </a>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="relative z-10 px-6 pb-6 pointer-events-auto">
        {!available && !activeRide && (
          <div className="max-w-md mx-auto mt-10 text-center">
            <div className="glass-panel rounded-2xl p-8">
              <Power className="w-16 h-16 text-white/40 mx-auto mb-4" />
              <h2 className="font-outfit font-bold text-2xl mb-2">You&apos;re Offline</h2>
              <p className="text-white/60 mb-6">Go online to start accepting ride requests</p>
              <button
                onClick={toggleAvailability}
                className="bg-neon-cyan text-black hover:bg-[#00B8D4] rounded-full font-bold px-8 py-4 shadow-[0_0_15px_rgba(0,224,255,0.4)] transition-all hover:scale-105"
                data-testid="go-online-btn"
              >
                Go Online
              </button>
            </div>
          </div>
        )}

        {/* Nearby Rides */}
        {available && !activeRide && nearbyRides.length > 0 && (
          <div className="max-w-2xl mx-auto mt-6 space-y-4">
            <h2 className="font-outfit font-bold text-2xl mb-4">Nearby Ride Requests</h2>
            {nearbyRides.map((ride) => (
              <div key={ride.id} className="glass-panel rounded-2xl p-6" data-testid={`nearby-ride-${ride.id}`}>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-neon-cyan mt-1" />
                    <div>
                      <div className="text-sm text-white/60">Pickup</div>
                      <div className="font-medium">{ride.pickup_address}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Navigation className="w-5 h-5 text-neon-purple mt-1" />
                    <div>
                      <div className="text-sm text-white/60">Dropoff</div>
                      <div className="font-medium">{ride.dropoff_address}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4 pt-4 border-t border-white/10">
                    <div>
                      <div className="text-sm text-white/60 mb-1">Distance</div>
                      <div className="font-mono font-bold">{ride.distance.toFixed(1)} miles</div>
                    </div>
                    <div>
                      <div className="text-sm text-white/60 mb-1">ETA</div>
                      <div className="font-mono font-bold">{ride.eta} min</div>
                    </div>
                    <div>
                      <div className="text-sm text-white/60 mb-1">Fare</div>
                      <div className="font-mono font-bold text-neon-cyan">${ride.fare}</div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleAcceptRide(ride.id)}
                    className="w-full bg-neon-cyan text-black hover:bg-[#00B8D4] rounded-full font-bold py-4 shadow-[0_0_15px_rgba(0,224,255,0.4)] transition-all hover:scale-105"
                    data-testid={`accept-ride-btn-${ride.id}`}
                  >
                    Accept Ride
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {available && !activeRide && nearbyRides.length === 0 && (
          <div className="max-w-md mx-auto mt-10 text-center">
            <div className="glass-panel rounded-2xl p-8">
              <MapPin className="w-16 h-16 text-white/40 mx-auto mb-4" />
              <h2 className="font-outfit font-bold text-2xl mb-2">No Nearby Rides</h2>
              <p className="text-white/60">Waiting for ride requests in your area...</p>
            </div>
          </div>
        )}

        {/* Active Ride Card */}
        {activeRide && (
          <div className="max-w-2xl mx-auto mt-6">
            <div className="glass-panel rounded-2xl p-6" data-testid="active-ride-card">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-outfit font-bold text-2xl">Active Ride</h2>
                <div className={`font-mono text-lg ${getStatusColor(activeRide.status)}`} data-testid="ride-status">
                  {formatStatus(activeRide.status)}
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <User className="w-5 h-5 text-green-400 mt-1" />
                  <div>
                    <div className="text-sm text-white/60">Rider</div>
                    <div className="font-medium">{activeRide.rider_name}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-neon-cyan mt-1" />
                  <div>
                    <div className="text-sm text-white/60">Pickup</div>
                    <div className="font-medium">{activeRide.pickup_address}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Navigation className="w-5 h-5 text-neon-purple mt-1" />
                  <div>
                    <div className="text-sm text-white/60">Dropoff</div>
                    <div className="font-medium">{activeRide.dropoff_address}</div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4 pt-4 border-t border-white/10">
                  <div>
                    <div className="text-sm text-white/60 mb-1">Distance</div>
                    <div className="font-mono font-bold">{activeRide.distance.toFixed(1)} miles</div>
                  </div>
                  <div>
                    <div className="text-sm text-white/60 mb-1">ETA</div>
                    <div className="font-mono font-bold">{activeRide.eta} min</div>
                  </div>
                  <div>
                    <div className="text-sm text-white/60 mb-1">Fare</div>
                    <div className="font-mono font-bold text-neon-cyan">${activeRide.fare}</div>
                  </div>
                </div>

                {getNextStatusAction() && (
                  <button
                    onClick={() => handleUpdateStatus(getNextStatusAction().next)}
                    className="w-full bg-neon-cyan text-black hover:bg-[#00B8D4] rounded-full font-bold py-4 flex items-center justify-center gap-2 shadow-[0_0_15px_rgba(0,224,255,0.4)] transition-all hover:scale-105 mt-4"
                    data-testid="next-status-btn"
                  >
                    {React.createElement(getNextStatusAction().icon, { className: 'w-5 h-5' })}
                    {getNextStatusAction().label}
                  </button>
                )}

                {/* Google Maps Navigation Button - Shows full route: Driver -> Pickup -> Dropoff */}
                {activeRide.status !== 'completed' && activeRide.status !== 'cancelled' && (
                  <button
                    onClick={openGoogleMapsNavigation}
                    className="w-full bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 rounded-full font-medium py-3 mt-4 transition-colors flex items-center justify-center gap-2"
                    data-testid="navigate-btn"
                  >
                    <ExternalLink className="w-5 h-5" />
                    Navigate Full Route
                  </button>
                )}

                {activeRide.status !== 'completed' && (
                  <button
                    onClick={() => {
                      setShowChat(true);
                      setUnreadMessages(0);
                    }}
                    className="w-full bg-neon-purple/20 text-neon-purple hover:bg-neon-purple/30 rounded-full font-medium py-3 mt-4 transition-colors flex items-center justify-center gap-2 relative"
                    data-testid="open-chat-btn"
                  >
                    <MessageCircle className="w-5 h-5" />
                    Chat with Rider
                    {unreadMessages > 0 && (
                      <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center animate-pulse">
                        {unreadMessages > 9 ? '9+' : unreadMessages}
                      </span>
                    )}
                  </button>
                )}

                {activeRide.status !== 'completed' && activeRide.status !== 'cancelled' && (
                  <button
                    onClick={() => setShowCancelModal(true)}
                    className="w-full bg-orange-500/20 text-orange-400 hover:bg-orange-500/30 rounded-full font-medium py-3 mt-2 transition-colors"
                    data-testid="reject-ride-btn"
                  >
                    Reject Ride
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Chat Box */}
        {showChat && activeRide && (
          <ChatBox
            ride={activeRide}
            socket={socketRef.current}
            currentUserRole="driver"
            onClose={() => setShowChat(false)}
          />
        )}

        {/* Account Settings Modal */}
        {showAccountSettings && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-start sm:items-center justify-center p-4 sm:p-6 pt-20 sm:pt-6 overflow-y-auto">
            <div className="glass-panel rounded-2xl p-4 sm:p-6 w-full max-w-md max-h-[85vh] sm:max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4 sm:mb-6 gap-2">
                <h2 className="font-outfit font-bold text-xl sm:text-2xl">Account Settings</h2>
                <div className="flex items-center gap-1 sm:gap-2">
                  {!isEditingAccount ? (
                    <button
                      onClick={() => setIsEditingAccount(true)}
                      className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 bg-neon-cyan/20 text-neon-cyan hover:bg-neon-cyan hover:text-black rounded-lg transition-all text-sm"
                      data-testid="edit-account-btn"
                    >
                      <Pencil className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      <span className="hidden xs:inline">Edit</span>
                    </button>
                  ) : (
                    <button
                      onClick={() => {
                        setIsEditingAccount(false);
                        // Reset form to original values
                        const names = user.name?.split(' ') || ['', ''];
                        setAccountForm({
                          first_name: names[0] || '',
                          last_name: names.slice(1).join(' ') || '',
                          email: user.email || '',
                          username: user.username || '',
                          phone: user.phone || '',
                          current_password: '',
                          new_password: '',
                          confirm_password: '',
                        });
                      }}
                      className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 bg-white/10 text-white/70 hover:bg-white/20 rounded-lg transition-all text-sm"
                      data-testid="cancel-edit-btn"
                    >
                      <span className="hidden xs:inline">Cancel</span>
                      <X className="w-3.5 h-3.5 xs:hidden" />
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setShowAccountSettings(false);
                      setIsEditingAccount(false);
                    }}
                    className="p-1.5 sm:p-2 hover:bg-white/10 rounded-lg transition-colors"
                  >
                    <X className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                </div>
              </div>

              {/* Read-Only View */}
              {!isEditingAccount ? (
                <div className="space-y-4">
                  {/* Profile Info Display */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 p-4 bg-dark-subtle rounded-xl">
                      <User className="w-5 h-5 text-neon-cyan flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-white/50 mb-1">Full Name</p>
                        <p className="text-white font-medium truncate">{user.name || 'Not set'}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 p-4 bg-dark-subtle rounded-xl">
                      <AtSign className="w-5 h-5 text-neon-purple flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-white/50 mb-1">Username</p>
                        <p className="text-white font-medium truncate">@{user.username || 'Not set'}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 p-4 bg-dark-subtle rounded-xl">
                      <Mail className="w-5 h-5 text-blue-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-white/50 mb-1">Email Address</p>
                        <p className="text-white font-medium truncate">{user.email || 'Not set'}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 p-4 bg-dark-subtle rounded-xl">
                      <Phone className="w-5 h-5 text-green-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-white/50 mb-1">Phone Number</p>
                        <p className="text-white font-medium truncate">{user.phone || 'Not set'}</p>
                      </div>
                    </div>
                  </div>

                  <p className="text-center text-white/40 text-sm mt-4">
                    Click the Edit button to modify your details
                  </p>
                </div>
              ) : (
                /* Edit Mode */
                <div className="space-y-4">
                  {/* Name Section */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm mb-2 text-white/80">First Name *</label>
                      <input
                        type="text"
                        required
                        value={accountForm.first_name}
                        onChange={(e) => setAccountForm({...accountForm, first_name: e.target.value})}
                        className="w-full bg-dark-subtle border border-white/10 focus:border-neon-cyan focus:ring-1 focus:ring-neon-cyan rounded-xl p-3 text-white"
                        data-testid="input-first-name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm mb-2 text-white/80">Last Name *</label>
                      <input
                        type="text"
                        required
                        value={accountForm.last_name}
                        onChange={(e) => setAccountForm({...accountForm, last_name: e.target.value})}
                        className="w-full bg-dark-subtle border border-white/10 focus:border-neon-cyan focus:ring-1 focus:ring-neon-cyan rounded-xl p-3 text-white"
                        data-testid="input-last-name"
                      />
                    </div>
                  </div>

                  {/* Contact Information Section */}
                  <div className="pt-4 border-t border-white/10">
                    <p className="text-sm font-medium text-white mb-3">Contact Information</p>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm mb-2 text-white/80">Email Address</label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                          <input
                            type="email"
                            value={accountForm.email}
                            onChange={(e) => setAccountForm({...accountForm, email: e.target.value})}
                            className="w-full bg-dark-subtle border border-white/10 focus:border-neon-cyan focus:ring-1 focus:ring-neon-cyan rounded-xl p-3 pl-10 text-white"
                            placeholder="your@email.com"
                            data-testid="input-email"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm mb-2 text-white/80">Username</label>
                        <div className="relative">
                          <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                          <input
                            type="text"
                            value={accountForm.username}
                            onChange={(e) => setAccountForm({...accountForm, username: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '')})}
                            className="w-full bg-dark-subtle border border-white/10 focus:border-neon-cyan focus:ring-1 focus:ring-neon-cyan rounded-xl p-3 pl-10 text-white"
                            placeholder="username"
                            data-testid="input-username"
                          />
                        </div>
                        <p className="text-xs text-white/40 mt-1">Letters, numbers, and underscores only</p>
                      </div>
                      <div>
                        <label className="block text-sm mb-2 text-white/80">Phone Number</label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                          <input
                            type="tel"
                            value={accountForm.phone}
                            onChange={(e) => setAccountForm({...accountForm, phone: e.target.value.replace(/[^0-9+\-\s]/g, '')})}
                            className="w-full bg-dark-subtle border border-white/10 focus:border-neon-cyan focus:ring-1 focus:ring-neon-cyan rounded-xl p-3 pl-10 text-white"
                            placeholder="Your phone number"
                            data-testid="input-phone"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Password Required Section */}
                  <div className="pt-4 border-t border-white/10">
                    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-3 mb-3">
                      <p className="text-sm text-yellow-400">Current password required to save any changes</p>
                    </div>
                    <input
                      type="password"
                      placeholder="Enter current password *"
                      value={accountForm.current_password}
                      onChange={(e) => setAccountForm({...accountForm, current_password: e.target.value})}
                      className="w-full bg-dark-subtle border border-white/10 focus:border-neon-cyan focus:ring-1 focus:ring-neon-cyan rounded-xl p-3 text-white placeholder:text-white/40"
                      data-testid="input-current-password"
                    />
                  </div>

                  {/* Change Password Section */}
                  <div className="pt-4 border-t border-white/10">
                    <p className="text-sm text-white/60 mb-3">Change Password (Optional)</p>
                    <div className="space-y-3">
                      <input
                        type="password"
                        placeholder="New Password (min 6 characters)"
                        value={accountForm.new_password}
                        onChange={(e) => setAccountForm({...accountForm, new_password: e.target.value})}
                        className="w-full bg-dark-subtle border border-white/10 focus:border-neon-cyan focus:ring-1 focus:ring-neon-cyan rounded-xl p-3 text-white placeholder:text-white/40"
                        data-testid="input-new-password"
                      />
                      <input
                        type="password"
                        placeholder="Confirm New Password"
                        value={accountForm.confirm_password}
                        onChange={(e) => setAccountForm({...accountForm, confirm_password: e.target.value})}
                        className="w-full bg-dark-subtle border border-white/10 focus:border-neon-cyan focus:ring-1 focus:ring-neon-cyan rounded-xl p-3 text-white placeholder:text-white/40"
                        data-testid="input-confirm-password"
                      />
                    </div>
                  </div>

                  <button
                    onClick={handleUpdateAccount}
                    className="w-full bg-neon-cyan text-black hover:bg-[#00B8D4] rounded-full font-bold py-4 shadow-[0_0_15px_rgba(0,224,255,0.4)] transition-all hover:scale-105 flex items-center justify-center gap-2"
                    data-testid="save-account-btn"
                  >
                    <Check className="w-5 h-5" />
                    Save Changes
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Reject Ride Modal */}
        {showCancelModal && activeRide && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6">
            <div className="glass-panel rounded-2xl p-6 w-full max-w-md">
              <h2 className="font-outfit font-bold text-2xl mb-4">Reject Ride</h2>
              <p className="text-white/60 mb-4">Please provide a reason for rejection. The ride will be returned to available rides for other drivers:</p>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="e.g., Vehicle issue, Emergency, Too far away..."
                className="w-full bg-dark-subtle border-transparent focus:border-neon-cyan focus:ring-1 focus:ring-neon-cyan rounded-xl p-3 text-white placeholder:text-white/40 mb-4"
                rows="3"
                data-testid="reject-reason-input"
              />
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowCancelModal(false);
                    setCancelReason('');
                  }}
                  className="flex-1 bg-white/10 hover:bg-white/20 rounded-full py-3 transition-colors"
                  data-testid="keep-ride-btn"
                >
                  Keep Ride
                </button>
                <button
                  onClick={handleRejectRide}
                  disabled={!cancelReason.trim()}
                  className="flex-1 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-full font-bold py-3 transition-all"
                  data-testid="confirm-reject-btn"
                >
                  Reject Ride
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Ride History Sidebar */}
        {showHistory && (
          <div className="fixed top-0 right-0 h-full w-full max-w-md bg-dark-surface border-l border-white/10 z-50 overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-outfit font-bold text-2xl">Ride History</h2>
                <button
                  onClick={() => setShowHistory(false)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  data-testid="close-history-btn"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-3">
                {rideHistory.length === 0 ? (
                  <div className="text-center py-10 text-white/60">No rides yet</div>
                ) : (
                  rideHistory.map((ride, idx) => (
                    <div key={ride.id} className="bg-dark-bg rounded-xl p-4" data-testid={`history-ride-${idx}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className={`text-sm font-mono ${getStatusColor(ride.status)}`}>
                          {formatStatus(ride.status)}
                        </div>
                        <div className="text-sm text-neon-cyan font-bold">${ride.fare}</div>
                      </div>
                      <div className="text-sm text-white/80">
                        <div className="truncate">{ride.pickup_address}</div>
                        <div className="text-white/60">→</div>
                        <div className="truncate">{ride.dropoff_address}</div>
                      </div>
                      <div className="text-xs text-white/40 mt-2">
                        {new Date(ride.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* Scheduled Rides Modal */}
        {showScheduledRides && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center p-4 pt-16 overflow-y-auto">
            <div className="glass-panel rounded-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
              {/* Header */}
              <div className="p-4 sm:p-6 border-b border-white/10 flex-shrink-0">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <Calendar className="w-6 h-6 text-purple-400" />
                    <h2 className="font-outfit font-bold text-xl sm:text-2xl">Scheduled Rides</h2>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setShowRouteMap(!showRouteMap)}
                      className={`p-2 rounded-lg transition-colors ${showRouteMap ? 'bg-purple-500 text-white' : 'hover:bg-white/10'}`}
                      title="Toggle Route Map"
                    >
                      <Route className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setShowScheduledRides(false)}
                      className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>
                
                {/* Date Selector & Stats */}
                <div className="mt-4 flex flex-wrap items-center gap-4">
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="bg-dark-subtle border border-white/10 rounded-xl px-4 py-2 text-white"
                  />
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-white/60" />
                      <span className="text-white/60">{getRidesForDate(selectedDate).length} rides</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-green-400" />
                      <span className="text-green-400 font-medium">${calculateTotalEarnings()}</span>
                      <span className="text-white/40">est. earnings</span>
                    </div>
                  </div>
                </div>

                {/* Conflict Warning */}
                {checkRideConflicts(scheduledRides).length > 0 && (
                  <div className="mt-4 bg-yellow-500/20 border border-yellow-500/30 rounded-xl p-3 flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-yellow-400 font-medium text-sm">Time Conflicts Detected</p>
                      <p className="text-white/60 text-xs mt-1">
                        {checkRideConflicts(scheduledRides).length} ride(s) may overlap. Reorder rides to resolve conflicts.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-4 sm:p-6">
                {showRouteMap && scheduledRides.length > 0 ? (
                  /* Route Map View */
                  <div className="h-96 rounded-xl overflow-hidden mb-4">
                    {mapsLoaded ? (
                      <GoogleMap
                        mapContainerStyle={{ height: '100%', width: '100%' }}
                        center={{ lat: currentLocation.latitude, lng: currentLocation.longitude }}
                        zoom={11}
                        options={{
                          disableDefaultUI: true,
                          zoomControl: true,
                          styles: mapStyles,
                        }}
                      >
                        {/* Driver Location */}
                        <Marker 
                          position={{ lat: currentLocation.latitude, lng: currentLocation.longitude }} 
                          icon={{ url: 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png' }}
                          title="Your Location"
                        />
                        {/* Ride Markers */}
                        {optimizedRoute.map((ride, index) => (
                          <React.Fragment key={ride.id}>
                            <Marker 
                              position={{ lat: ride.pickup_lat, lng: ride.pickup_lng }} 
                              icon={{ url: 'https://maps.google.com/mapfiles/ms/icons/green-dot.png' }}
                              title={`Pickup #${index + 1}: ${ride.pickup_address}`}
                            />
                            <Marker 
                              position={{ lat: ride.dropoff_lat, lng: ride.dropoff_lng }} 
                              icon={{ url: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png' }}
                              title={`Dropoff #${index + 1}: ${ride.dropoff_address}`}
                            />
                          </React.Fragment>
                        ))}
                      </GoogleMap>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-dark-surface">
                        <div className="text-neon-purple animate-pulse">Loading Map...</div>
                      </div>
                    )}
                    <div className="mt-3 flex items-center gap-2 text-xs text-white/60">
                      <Route className="w-4 h-4" />
                      <span>Optimized route shown. Green = Pickup, Red = Dropoff.</span>
                    </div>
                  </div>
                ) : (
                  /* Timeline View */
                  <div className="space-y-3">
                    {/* Pending Assigned Ride Requests Section */}
                    {pendingAssignedRides.length > 0 && (
                      <div className="mb-6">
                        <div className="bg-yellow-500/20 border border-yellow-500/30 rounded-xl p-4">
                          <div className="flex items-center gap-3 mb-3">
                            <AlertCircle className="w-5 h-5 text-yellow-400" />
                            <span className="font-medium text-yellow-400">
                              Ride Requests for You ({pendingAssignedRides.length})
                            </span>
                          </div>
                          <p className="text-white/60 text-sm mb-4">
                            These riders have specifically requested you as their driver.
                          </p>
                          <div className="space-y-3">
                            {pendingAssignedRides.map(ride => (
                              <div key={ride.id} className="bg-dark-bg rounded-xl p-4 border border-yellow-500/20">
                                <div className="flex items-start justify-between gap-3 mb-3">
                                  <div>
                                    <p className="font-medium text-white">{ride.rider_name}</p>
                                    <p className="text-xs text-white/50">@{ride.rider_username}</p>
                                  </div>
                                  <span className="text-lg font-bold text-green-400">${ride.fare}</span>
                                </div>
                                
                                <div className="space-y-2 text-sm mb-3">
                                  <div className="flex items-start gap-2">
                                    <MapPin className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                                    <span className="text-white/80">{ride.pickup_address}</span>
                                  </div>
                                  <div className="flex items-start gap-2">
                                    <MapPin className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                                    <span className="text-white/80">{ride.dropoff_address}</span>
                                  </div>
                                  {ride.scheduled_time && (
                                    <div className="flex items-center gap-2 text-white/60">
                                      <Calendar className="w-4 h-4" />
                                      <span>{new Date(ride.scheduled_time).toLocaleString()}</span>
                                    </div>
                                  )}
                                </div>
                                
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => handleAcceptAssignedRide(ride.id)}
                                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-xl font-medium transition-all"
                                    data-testid={`accept-assigned-${ride.id}`}
                                  >
                                    <Check className="w-4 h-4" />
                                    Accept
                                  </button>
                                  <button
                                    onClick={() => handleDeclineAssignedRide(ride.id)}
                                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-xl font-medium transition-all"
                                    data-testid={`decline-assigned-${ride.id}`}
                                  >
                                    <X className="w-4 h-4" />
                                    Decline
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {scheduledRides.length === 0 && pendingAssignedRides.length === 0 ? (
                      <div className="text-center py-12">
                        <Calendar className="w-16 h-16 text-white/20 mx-auto mb-4" />
                        <p className="text-white/60">No scheduled rides yet</p>
                        <p className="text-white/40 text-sm mt-2">Accept scheduled rides to see them here</p>
                      </div>
                    ) : (
                      <>
                        {/* Suggested Order Banner */}
                        {optimizedRoute.length > 0 && (
                          <div className="bg-purple-500/20 border border-purple-500/30 rounded-xl p-4 mb-4">
                            <div className="flex items-center gap-3 mb-2">
                              <Route className="w-5 h-5 text-purple-400" />
                              <span className="font-medium text-purple-400">Suggested Order</span>
                            </div>
                            <p className="text-white/60 text-sm">
                              Based on pickup locations and scheduled times, we recommend completing rides in this order for optimal efficiency.
                            </p>
                            <button
                              onClick={() => setScheduledRides([...optimizedRoute])}
                              className="mt-3 text-sm text-purple-400 hover:text-purple-300 flex items-center gap-2"
                            >
                              <Check className="w-4 h-4" />
                              Apply suggested order
                            </button>
                          </div>
                        )}

                        {/* Timeline */}
                        <div className="relative">
                          {/* Timeline line */}
                          <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-white/10"></div>
                          
                          {scheduledRides.map((ride, index) => {
                            const conflicts = checkRideConflicts(scheduledRides);
                            const hasConflict = conflicts.some(c => c.ride1.id === ride.id || c.ride2.id === ride.id);
                            const isUrgent = ride.scheduled_time && (new Date(ride.scheduled_time).getTime() - Date.now()) < 30 * 60 * 1000;
                            
                            return (
                              <div
                                key={ride.id}
                                className={`relative pl-14 pb-4 ${draggedRide === ride.id ? 'opacity-50' : ''}`}
                                draggable
                                onDragStart={() => setDraggedRide(ride.id)}
                                onDragEnd={() => setDraggedRide(null)}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={() => {
                                  if (draggedRide && draggedRide !== ride.id) {
                                    const fromIndex = scheduledRides.findIndex(r => r.id === draggedRide);
                                    const toIndex = index;
                                    moveRide(fromIndex, toIndex);
                                  }
                                }}
                              >
                                {/* Timeline dot */}
                                <div className={`absolute left-4 w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                  hasConflict ? 'bg-yellow-500 border-yellow-400' :
                                  isUrgent ? 'bg-red-500 border-red-400' :
                                  'bg-purple-500 border-purple-400'
                                }`}>
                                  <span className="text-[10px] font-bold text-white">{index + 1}</span>
                                </div>
                                
                                {/* Ride Card */}
                                <div className={`glass-panel rounded-xl p-4 border ${
                                  hasConflict ? 'border-yellow-500/50' :
                                  isUrgent ? 'border-red-500/50' :
                                  'border-white/10'
                                }`}>
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="flex-1 min-w-0">
                                      {/* Time */}
                                      <div className="flex items-center gap-2 mb-2">
                                        <Clock className={`w-4 h-4 ${isUrgent ? 'text-red-400' : 'text-white/60'}`} />
                                        <span className={`font-medium ${isUrgent ? 'text-red-400' : ''}`}>
                                          {ride.scheduled_time 
                                            ? new Date(ride.scheduled_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                                            : 'Flexible time'}
                                        </span>
                                        {isUrgent && <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded-full">Soon</span>}
                                        {hasConflict && <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded-full">Conflict</span>}
                                      </div>
                                      
                                      {/* Locations */}
                                      <div className="space-y-1 text-sm">
                                        <div className="flex items-start gap-2">
                                          <MapPin className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                                          <span className="text-white/80 truncate">{ride.pickup_address}</span>
                                        </div>
                                        <div className="flex items-start gap-2">
                                          <Navigation className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                                          <span className="text-white/80 truncate">{ride.dropoff_address}</span>
                                        </div>
                                      </div>
                                      
                                      {/* Rider & Fare */}
                                      <div className="flex items-center gap-4 mt-3 text-sm">
                                        <div className="flex items-center gap-1 text-white/60">
                                          <User className="w-4 h-4" />
                                          <span>{ride.rider_name || 'Rider'}</span>
                                        </div>
                                        <div className="flex items-center gap-1 text-green-400">
                                          <DollarSign className="w-4 h-4" />
                                          <span className="font-medium">{ride.custom_fare || ride.fare}</span>
                                        </div>
                                        {ride.seats_requested > 1 && (
                                          <span className="text-white/40 text-xs">{ride.seats_requested} seats</span>
                                        )}
                                      </div>
                                    </div>
                                    
                                    {/* Actions */}
                                    <div className="flex flex-col gap-2">
                                      <button
                                        className="p-2 hover:bg-white/10 rounded-lg transition-colors cursor-grab active:cursor-grabbing"
                                        title="Drag to reorder"
                                      >
                                        <GripVertical className="w-4 h-4 text-white/40" />
                                      </button>
                                      {index > 0 && (
                                        <button
                                          onClick={() => moveRide(index, index - 1)}
                                          className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                                          title="Move up"
                                        >
                                          <ChevronUp className="w-4 h-4" />
                                        </button>
                                      )}
                                      {index < scheduledRides.length - 1 && (
                                        <button
                                          onClick={() => moveRide(index, index + 1)}
                                          className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                                          title="Move down"
                                        >
                                          <ChevronDown className="w-4 h-4" />
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                  
                                  {/* Quick Actions */}
                                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/10">
                                    <button
                                      onClick={() => {
                                        const url = `https://www.google.com/maps/dir/?api=1&origin=${currentLocation.latitude},${currentLocation.longitude}&destination=${ride.pickup_lat},${ride.pickup_lng}&travelmode=driving`;
                                        window.open(url, '_blank');
                                      }}
                                      className="flex-1 flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 rounded-lg py-2 text-sm transition-colors"
                                    >
                                      <Navigation className="w-4 h-4" />
                                      <span>Navigate</span>
                                    </button>
                                    <button
                                      onClick={() => {
                                        setActiveRide(ride);
                                        setShowScheduledRides(false);
                                        toast.success('Ride selected. Update status when ready.');
                                      }}
                                      className="flex-1 flex items-center justify-center gap-2 bg-purple-500 hover:bg-purple-600 rounded-lg py-2 text-sm transition-colors"
                                    >
                                      <Play className="w-4 h-4" />
                                      <span>Start Ride</span>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-white/10 flex-shrink-0">
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => {
                      navigate('/rides-available');
                      setShowScheduledRides(false);
                    }}
                    className="flex items-center gap-2 text-neon-cyan hover:text-white transition-colors"
                  >
                    <List className="w-4 h-4" />
                    <span>Browse Available Rides</span>
                  </button>
                  <button
                    onClick={fetchScheduledRides}
                    className="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
                  >
                    <RefreshCw className="w-4 h-4" />
                    <span>Refresh</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      </div>
    </div>
  );
}